import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, query, where, collection, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { raiseDispute, respondToDispute } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to manage disputes for a raffle
 *
 * @param {string} raffleId - The raffle ID
 * @returns {object} - Dispute state and actions
 */
export function useDispute(raffleId) {
  const { user } = useAuth();
  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Subscribe to dispute for this raffle where user is a participant
  useEffect(() => {
    if (!raffleId || !user?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Query for dispute on this raffle where user is a participant
    // Must use array-contains to satisfy Firestore rules
    const disputesRef = collection(db, 'disputes');
    const q = query(
      disputesRef,
      where('raffleId', '==', raffleId),
      where('participants', 'array-contains', user.uid),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!snapshot.empty) {
          const disputeDoc = snapshot.docs[0];
          const data = disputeDoc.data();
          setDispute({ id: disputeDoc.id, ...data });
        } else {
          setDispute(null);
        }
        setLoading(false);
      },
      (err) => {
        // Permission errors are expected if no dispute exists yet
        // or if user is not a participant
        if (err.code !== 'permission-denied') {
          console.error('Error fetching dispute:', err);
        }
        setDispute(null);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [raffleId, user?.uid]);

  // Raise a new dispute
  const raise = useCallback(
    async (reason, description, evidence = []) => {
      if (!user?.uid || !raffleId) {
        return { success: false, error: 'Not authenticated' };
      }

      setSubmitting(true);
      setError(null);

      try {
        const result = await raiseDispute({
          raffleId,
          reason,
          description,
          evidence,
        });

        if (result.success) {
          return { success: true, disputeId: result.disputeId };
        } else {
          setError(result.error || 'Failed to raise dispute');
          return { success: false, error: result.error };
        }
      } catch (err) {
        const message = err.message || 'Failed to raise dispute';
        setError(message);
        return { success: false, error: message };
      } finally {
        setSubmitting(false);
      }
    },
    [raffleId, user?.uid]
  );

  // Respond to an existing dispute
  const respond = useCallback(
    async (description, evidence = []) => {
      if (!user?.uid || !dispute?.id) {
        return { success: false, error: 'No dispute to respond to' };
      }

      setSubmitting(true);
      setError(null);

      try {
        const result = await respondToDispute({
          disputeId: dispute.id,
          description,
          evidence,
        });

        if (result.success) {
          return { success: true };
        } else {
          setError(result.error || 'Failed to respond to dispute');
          return { success: false, error: result.error };
        }
      } catch (err) {
        const message = err.message || 'Failed to respond to dispute';
        setError(message);
        return { success: false, error: message };
      } finally {
        setSubmitting(false);
      }
    },
    [dispute?.id, user?.uid]
  );

  // Check if user can raise a dispute
  const canRaiseDispute = !dispute && !loading;

  // Check if user needs to respond
  const needsResponse = dispute?.status === 'open' && dispute?.againstUser === user?.uid && !dispute?.respondedAt;

  // Check if user raised this dispute
  const isRaiser = dispute?.raisedBy === user?.uid;

  return {
    dispute,
    loading,
    submitting,
    error,
    raise,
    respond,
    canRaiseDispute,
    needsResponse,
    isRaiser,
  };
}

export default useDispute;
